import { useRef, useEffect } from 'react';

export interface Clipboard {
  copy: (text: string) => Promise<void>;
}

export const useClipboard = (): Clipboard => {
  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    const input = document.createElement('input');
    // preserve line breaks
    input.style.whiteSpace = 'pre';

    /**
     * The input must be hidden but without using display: 'none' or visibility: 'hidden'
     * because copy won't work.
     */
    input.style.position = 'fixed';
    input.style.top = '0';
    input.style.clip = 'rect(0, 0, 0, 0)';

    document.body.appendChild(input);
    inputRef.current = input;

    return () => {
      document.body.removeChild(input);
    };
  }, []);

  /**
   * If the modern API is supported by the browser, it is used. Otherwise, the
   * deprecated method is used.
   */
  const copy = async (text: string): Promise<void> => {
    const isClipboardApiSupported = navigator.clipboard !== undefined;
    if (isClipboardApiSupported) {
      return navigator.clipboard.writeText(text);
    } else {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      input.value = text;
      input.select();
      document.execCommand('copy');
    }
  };

  return {
    copy,
  };
};
